import React from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import ParticleCanvas from "./ParticleCanvas";
import AnimatedHeading from "./AnimatedHeading";
import PrimaryCTA from "./PrimaryCTA";
import SecondaryCTA from "./SecondaryCTA";
import useMotionGuard from "../hooks/useMotionGuard";

/**
 * Hero con parallax mouse-move sottile su eyebrow / H1 / subtitle.
 * Attenuato: eyebrow ±3px, H1 ±8px, sub ±4px — desiderato "sottile, non vistoso".
 * Disattivato su touch e reduced-motion.
 */
export default function Hero({ onPrimary, onSecondary }) {
    const { disabled } = useMotionGuard();

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const smx = useSpring(mx, { stiffness: 55, damping: 20, mass: 0.7 });
    const smy = useSpring(my, { stiffness: 55, damping: 20, mass: 0.7 });

    const eyebrowX = useTransform(smx, [-1, 1], [-3, 3]);
    const eyebrowY = useTransform(smy, [-1, 1], [-2, 2]);
    const h1X = useTransform(smx, [-1, 1], [-8, 8]);
    const h1Y = useTransform(smy, [-1, 1], [-4, 4]);
    const subX = useTransform(smx, [-1, 1], [-4, 4]);
    const subY = useTransform(smy, [-1, 1], [-2.5, 2.5]);

    const onMouseMove = (e) => {
        if (disabled) return;
        const w = window.innerWidth || 1;
        const h = window.innerHeight || 1;
        mx.set((e.clientX / w) * 2 - 1);
        my.set((e.clientY / h) * 2 - 1);
    };
    const onMouseLeave = () => {
        mx.set(0);
        my.set(0);
    };

    return (
        <header
            className="lp-hero"
            role="banner"
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
        >
            <ParticleCanvas />
            <div className="lp-hero-glow" aria-hidden="true" />
            <div className="lp-container lp-hero-inner">
                <motion.div style={disabled ? undefined : { x: eyebrowX, y: eyebrowY }}>
                    <motion.div
                        className="lp-eyebrow lp-hero-eyebrow"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        data-testid="hero-eyebrow"
                    >
                        <span>Energia · Full service</span>
                    </motion.div>
                </motion.div>

                <motion.div style={disabled ? undefined : { x: h1X, y: h1Y }}>
                    <AnimatedHeading
                        as="h1"
                        className="lp-h1"
                        text="Vuoi vendere"
                        data-testid="hero-h1"
                    />
                    <AnimatedHeading
                        as="div"
                        className="lp-h1"
                        text="energia?"
                        delay={0.08}
                    />
                    <AnimatedHeading
                        as="div"
                        className="lp-h1"
                        text="Costruiamo noi"
                        delay={0.16}
                    />
                    <AnimatedHeading
                        as="div"
                        className="lp-h1"
                        text="tutto quello"
                        delay={0.24}
                    />
                    <AnimatedHeading
                        as="div"
                        className="lp-h1"
                        text="che c'è"
                        delay={0.32}
                    />
                    <AnimatedHeading
                        as="div"
                        className="lp-h1 lp-hero-h1"
                        text="dietro."
                        delay={0.40}
                    />
                </motion.div>

                <motion.div style={disabled ? undefined : { x: subX, y: subY }}>
                    <motion.p
                        className="lp-body-lg lp-hero-sub"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
                    >
                        Piattaforma, back-office, contabilità, legale, IT. Un'unica macchina, chiavi in mano, per operatori che partono da zero.
                    </motion.p>
                </motion.div>

                <motion.div
                    className="lp-hero-ctas"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.55 }}
                >
                    <PrimaryCTA onClick={onPrimary} dataTestId="hero-cta">Fatti richiamare</PrimaryCTA>
                    <SecondaryCTA onClick={onSecondary} dataTestId="hero-cta-secondary">Scopri il metodo</SecondaryCTA>
                </motion.div>
            </div>
        </header>
    );
}
